/**
 * ResourcesSection Component
 * Session-scoped resources display with search and category filter. The parent owns Add Resource.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Stack,
  Group,
  Text,
  Badge,
  Loader,
  Center,
  Table,
  Paper,
  ThemeIcon,
  TextInput,
  Select,
  ActionIcon,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Search, ExternalLink, Download, Folder, Trash2 } from 'react-feather';
import { resourcesApi } from '../../../../services/api/resources';
import type { ResourceWithDetails, ResourceCategory } from '../../../../types';
import { formatDate } from '../../../../utils/formatters';

interface ResourcesSectionProps {
  cohortId: string;
  sessionId: string;
  categories: ResourceCategory[];
  onResourceAdded?: () => void;
  /** MED-1393: the unfiltered count, for the collapsed section header. */
  onCountChange?: (count: number) => void;
  /** When true, hides add/remove actions (read-only mode for facilitators) */
  readOnly?: boolean;
}

export function ResourcesSection({
  cohortId,
  sessionId,
  categories,
  onResourceAdded,
  onCountChange,
  readOnly = false,
}: ResourcesSectionProps) {
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState<ResourceWithDetails[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  // MED-1393: the staff route refuses a coach. activePersona stays 'admin' for a
  // coach login and an impersonation, so read the coach page route instead.
  const isCoach = useLocation().pathname.startsWith('/coach/');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // MED-1393: a late answer for an earlier session or search must not overwrite the latest.
  const requestRef = useRef(0);

  const loadData = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const filters = {
        session_ids: [sessionId],
        search: search || undefined,
        category_id: categoryFilter || undefined,
      };
      const data = isCoach
        ? await resourcesApi.getCoachResourcesForCohort(cohortId, filters)
        : await resourcesApi.getResourcesForCohort(cohortId, filters);
      // Filter out "General" resources (no session associations) — backend handles session matching
      // including "All Groups" resources with the same session_number
      if (request !== requestRef.current) return;
      const sessionScoped = data.data.filter(
        (r) => r.sessions && r.sessions.length > 0,
      );
      setResources(sessionScoped);
      if (!search && !categoryFilter) onCountChange?.(sessionScoped.length);
    } catch {
      if (request !== requestRef.current) return;
      // MED-1393: a failed read is not "no resources"; say it failed.
      setResources([]);
      setLoadFailed(true);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [cohortId, sessionId, search, categoryFilter, isCoach, onCountChange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // An unmounted copy (the remount after a save) must not report a late count.
  useEffect(() => () => void requestRef.current++, []);

  const handleDelete = (resource: ResourceWithDetails) => {
    modals.openConfirmModal({
      title: 'Remove Resource',
      children: (
        <Text size="sm">
          Are you sure you want to remove <strong>{resource.title}</strong> from this session?
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await resourcesApi.delete(resource.id);
          notifications.show({
            title: 'Resource removed',
            message: 'Resource removed successfully',
            color: 'green',
          });
          loadData();
          onResourceAdded?.();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to remove resource',
            color: 'red',
          });
        }
      },
    });
  };

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search resources..."
          leftSection={<Search size={14} />}
          size="xs"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        {/* MED-1393: the category list is staff-only, so a coach gets none; hide the empty filter. */}
        {categoryOptions.length > 0 && (
          <Select
            placeholder="Category"
            data={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            size="xs"
            clearable
            style={{ width: 180 }}
          />
        )}
      </Group>

      {resources.length === 0 ? (
        <Paper withBorder p="lg" style={{ borderStyle: 'dashed' }}>
          <Stack align="center" gap="sm">
            <ThemeIcon size={40} radius="xl" variant="light" color="gray">
              <Folder size={20} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" ta="center">
              {loadFailed
                ? 'Could not load resources.'
                : 'No resources attached to this session yet.'}
            </Text>
          </Stack>
        </Paper>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Title</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Access</Table.Th>
              <Table.Th>Added</Table.Th>
              <Table.Th style={{ width: readOnly ? 40 : 72 }}>Action</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {resources.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      {r.title}
                    </Text>
                    {r.is_evergreen && (
                      <Badge size="xs" color="teal" variant="light">
                        EVERGREEN
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {r.category_name || '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {r.access_coaches && (
                      <Badge size="xs" variant="light" color="blue">
                        Coach
                      </Badge>
                    )}
                    {r.access_participants && (
                      <Badge size="xs" variant="light" color="green">
                        Participant
                      </Badge>
                    )}
                    {r.access_partners && (
                      <Badge size="xs" variant="light" color="orange">
                        Partner
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {r.created_at ? formatDate(r.created_at) : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    {r.resource_type === 'link' && r.url ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        component="a"
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink size={14} />
                      </ActionIcon>
                    ) : r.resource_type === 'file' && r.download_url ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        component="a"
                        href={r.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Download size={14} />
                      </ActionIcon>
                    ) : null}
                    {!readOnly && !r.is_evergreen && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleDelete(r)}
                      >
                        <Trash2 size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

export default ResourcesSection;
